import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  output,
  Signal,
  ViewChild,
  type OnDestroy,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { NgCommonModule } from '@principle-theorem/ng-shared';
import {
  CustomChartType,
  ICustomReportFilter,
  ICustomReportFilterValue,
  type ICustomReportChart,
} from '@principle-theorem/principle-core/interfaces';
import { ReportBuilderHelpers } from '@principle-theorem/reporting';
import { snapshot, uid } from '@principle-theorem/shared';
import { compact, difference, uniqBy } from 'lodash';
import { combineLatest, EMPTY, Observable, ReplaySubject } from 'rxjs';
import {
  filter,
  map,
  pairwise,
  startWith,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { type IDCChartEvent } from '../../../../core/chart-builders/dc-chart-builders/dc-chart-builder';
import { DcChartComponent } from '../../../../core/dc-chart/dc-chart.component';
import {
  IResetChartFilter,
  ReportBuilderStore,
} from '../../../report-builder.store';
import { ReportBuilderTableChartComponent } from '../report-builder-table-chart/report-builder-table-chart.component';
import { type IChartDisplay } from './report-builder-chart-display';

const ICON_MAP: { [key in CustomChartType]: string } = {
  [CustomChartType.NumberSummary]: 'numbers',
  [CustomChartType.Table]: 'data_table',
  [CustomChartType.Line]: 'show_chart',
  [CustomChartType.Pie]: 'pie_chart',
  [CustomChartType.Bar]: 'bar_chart',
  [CustomChartType.Row]: 'sort',
  [CustomChartType.Column]: 'bar_chart',
  [CustomChartType.SelectMenu]: 'data_table',
  [CustomChartType.StackedArea]: 'monitoring',
  [CustomChartType.Histogram]: 'insert_chart',
  [CustomChartType.StackedBar]: 'stacked_bar_chart',
  [CustomChartType.SanKey]: 'insert_chart',
};

interface IPlaceholder {
  icon: string;
  message: string;
}

@Component({
  selector: 'pr-report-builder-chart',
  templateUrl: './report-builder-chart.component.html',
  styleUrls: ['./report-builder-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DcChartComponent,
    MatButtonModule,
    MatIcon,
    MatTooltip,
    NgCommonModule,
    ReportBuilderTableChartComponent,
  ],
})
export class ReportBuilderChartComponent implements OnDestroy {
  private _onDestroy$ = new ReplaySubject<void>(1);
  private _dcChart$ = new ReplaySubject<DcChartComponent>(1);
  store = inject(ReportBuilderStore);
  chart$ = new ReplaySubject<ICustomReportChart>(1);
  display$ = new ReplaySubject<IChartDisplay>(1);
  canResetFilter$: Observable<boolean>;
  placeholder: Signal<IPlaceholder | undefined>;

  @Input() sectionName: string;
  @Input({ transform: coerceBooleanProperty }) readOnly: boolean = false;

  edit = output<void>();

  @Input()
  set display(display: IChartDisplay) {
    if (display) {
      this.display$.next(display);
    }
  }

  @ViewChild('dcChart')
  set dcChart(dcChart: DcChartComponent) {
    if (dcChart) {
      this._dcChart$.next(dcChart);
    }
  }

  constructor() {
    this.display$
      .pipe(
        switchMap(
          (display) =>
            display.chartDisplay?.eventListener$ ??
            display.tableDisplay?.eventListener$ ??
            EMPTY
        ),
        startWith(undefined),
        pairwise(),
        takeUntil(this._onDestroy$)
      )
      .subscribe(
        ([previousEvent, currentEvent]) =>
          void this._updateChartFilter(previousEvent, currentEvent)
      );

    combineLatest([this._dcChart$, this.display$, this.store.resetFilter$])
      .pipe(
        filter(([_dcChart, display, reset]) =>
          this._resetAffectsThisChart(display, reset)
        ),
        takeUntilDestroyed()
      )
      .subscribe(([dcChart]) => void dcChart.resetFilter());

    this.canResetFilter$ = this._dcChart$.pipe(
      switchMap((dcChart) => dcChart.canResetFilter$)
    );

    const hasDisplay$ = this.display$.pipe(
      map((display) => !!display.chartDisplay || !!display.tableDisplay)
    );

    const isMissingDatapoints$ = combineLatest([
      this.display$,
      this.store.loadedDatapoints$,
    ]).pipe(
      map(([display, loadedDatapoints]) => {
        const requiredDatapoints = ReportBuilderHelpers.getDatapointsForChart(
          display.chart
        );
        return !ReportBuilderHelpers.hasRequiredDatapoints(
          loadedDatapoints,
          requiredDatapoints
        );
      })
    );

    const placeholder$ = combineLatest([
      this.display$,
      hasDisplay$,
      isMissingDatapoints$,
      this.store.hasNoData$,
    ]).pipe(
      map(([chartDisplay, hasDisplay, isMissingDatapoints, hasNoData]) =>
        this._getPlaceholder(
          chartDisplay,
          hasDisplay,
          isMissingDatapoints,
          hasNoData
        )
      )
    );

    this.placeholder = toSignal(placeholder$, { initialValue: undefined });
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  async resetFilter(): Promise<void> {
    const chart = await snapshot(this._dcChart$);
    await chart.resetFilter();
  }

  async getLabel(chart: ICustomReportChart): Promise<string> {
    if (chart.label) {
      return chart.label;
    }

    if (!chart.settings[0].dataPoint.endsWith('.count')) {
      return chart.settings[0].label;
    }

    const dataSource = await snapshot(this.store.dataSource$);
    const groupBys = dataSource?.groupByOptions ?? [];
    const groupBy = groupBys
      .flatMap((section) => section.options)
      .find(
        (availableGroupBy) =>
          availableGroupBy.measure.metadata.id === chart.groupBy
      )?.measure;

    return groupBy?.metadata?.label ?? 'Chart';
  }

  getChartIcon(display: IChartDisplay): string {
    return ICON_MAP[display.chart.type] || 'bar_chart';
  }

  private async _updateChartFilter(
    previousEvent?: IDCChartEvent,
    currentEvent?: IDCChartEvent
  ): Promise<void> {
    if (!currentEvent) {
      return;
    }

    const display = await snapshot(this.store.display$);
    if (!display) {
      return;
    }

    const filterChanges = this._getFilterValueChanges(
      currentEvent.filterValues,
      previousEvent?.filterValues
    );

    const updatedFilters = this._upsertFilterChanges(
      currentEvent.groupByDimension.metadata.id,
      display.dynamicFilters,
      filterChanges
    );

    this.store.setDisplay({
      ...display,
      dynamicFilters: updatedFilters,
    });

    if (currentEvent.isReset) {
      this.store.resetFilter$.next({
        dimensionId: currentEvent.groupByDimension.metadata.id,
      });
    }
  }

  private _getFilterValueChanges(
    after: ICustomReportFilterValue[],
    before: ICustomReportFilterValue[] = []
  ): IFilterChanges {
    const beforeValues = before.map((value) => value.value);
    const afterValues = after.map((value) => value.value);

    const addedValues = difference(afterValues, beforeValues);
    const removedValues = difference(beforeValues, afterValues);

    const added = addedValues.map((value) =>
      after.find((filterValue) => filterValue.value === value)
    );
    const removed = removedValues.map((value) =>
      before.find((filterValue) => filterValue.value === value)
    );

    return {
      added: compact(added),
      removed: compact(removed),
    };
  }

  private _upsertFilterChanges(
    dimensionId: string,
    filters: ICustomReportFilter[],
    changes: IFilterChanges
  ): ICustomReportFilter[] {
    const existingFilter = filters.find(
      (dynamicFilter) => dynamicFilter.id === dimensionId
    );

    if (!existingFilter) {
      if (!changes.added.length) {
        return filters;
      }
      const newFilter = {
        uid: uid(true),
        id: dimensionId,
        values: changes.added,
      };
      return [...filters, newFilter];
    }

    existingFilter.values = existingFilter.values.filter((filterValue) => {
      const filterIsRemoved = changes.removed.some(
        (removedFilter) => removedFilter.value === filterValue.value
      );
      return !filterIsRemoved;
    });

    existingFilter.values = uniqBy(
      [...existingFilter.values, ...changes.added],
      (filterValue) => filterValue.value
    );

    const updatedFilters = filters
      .map((dynamicFilter) =>
        dynamicFilter.uid === existingFilter.uid
          ? { ...existingFilter }
          : dynamicFilter
      )
      .filter((dynamicFilter) => dynamicFilter.values.length > 0);

    return updatedFilters;
  }

  private _resetAffectsThisChart(
    display: IChartDisplay,
    reset: IResetChartFilter
  ): boolean {
    if (reset.chartUid && reset.chartUid === display.chart.uid) {
      return true;
    }
    if (reset.dimensionId && reset.dimensionId === display.chart.groupBy) {
      return true;
    }
    return false;
  }

  private _getPlaceholder(
    chartDisplay: IChartDisplay,
    hasDisplay: boolean,
    isMissingDatapoints: boolean,
    hasNoData: boolean
  ): IPlaceholder | undefined {
    const icon = this.getChartIcon(chartDisplay);

    if (chartDisplay.chart.placeholderMessage) {
      return {
        icon: 'edit',
        message: 'Select a value before running the report',
      };
    }

    if (!hasDisplay) {
      return {
        icon,
        message: 'Run report to load data ',
      };
    }

    if (isMissingDatapoints) {
      return {
        icon,
        message: 'Required datapoints have not been loaded',
      };
    }

    if (hasNoData) {
      return {
        icon: 'manage_search',
        message: 'No data found',
      };
    }
  }
}

interface IFilterChanges {
  added: ICustomReportFilterValue[];
  removed: ICustomReportFilterValue[];
}
